/**
 * Pricing & Page Range Calculation Utility
 * Accurately parses custom page ranges (e.g. "2", "1-3", "1, 3, 5-8", "ALL")
 * and computes exact billable pages and costs. Clamps to document total pages
 * so that non-existent pages (e.g. page 3 on a 2-page document) cannot be billed or printed.
 */
/**
 * Parses a page range string and counts how many distinct valid pages will be printed.
 * Clamped strictly to totalPages (e.g. range "1-5" on a 2-page document returns 2).
 *
 * @param rangeStr The user-specified page range (e.g. "2", "1-5", "1,3,5-7", "ALL", "")
 * @param totalPages The total number of pages in the source document
 * @returns The number of pages that will physically be printed (clamped <= totalPages)
 */
export function countPagesInRange(rangeStr, totalPages = 1) {
    const docPages = Math.max(1, totalPages || 1);
    if (!rangeStr || !rangeStr.trim() || rangeStr.trim().toUpperCase() === 'ALL') {
        return docPages;
    }
    const clean = rangeStr.trim();
    const pages = new Set();
    const parts = clean.split(/[,;\s]+/);
    for (const part of parts) {
        const trimmed = part.trim();
        if (!trimmed)
            continue;
        // Check for range e.g. "1-5" or "1:5"
        const match = trimmed.match(/^(\d+)\s*[-:]\s*(\d+)$/);
        if (match) {
            const start = parseInt(match[1], 10);
            const end = parseInt(match[2], 10);
            if (!isNaN(start) && !isNaN(end)) {
                const min = Math.max(1, Math.min(start, end));
                // Strict clamp to docPages: never allow pages > docPages
                const max = Math.min(docPages, Math.max(start, end));
                for (let i = min; i <= max; i++) {
                    pages.add(i);
                }
            }
        }
        else {
            // Single page e.g. "2"
            const single = parseInt(trimmed, 10);
            if (!isNaN(single) && single >= 1 && single <= docPages) {
                pages.add(single);
            }
        }
    }
    // If user entered pages completely out of range (e.g. page 5 on a 2-page doc), default to 1 page
    if (pages.size === 0) {
        return 1;
    }
    return Math.min(docPages, pages.size);
}
/**
 * Normalizes and clamps a page range string so no page number exceeds totalPages.
 * e.g. "1-3" on a 2-page document becomes "1-2".
 * e.g. "3" on a 2-page document becomes "2".
 * e.g. "ALL" or blank returns "".
 */
export function clampPageRange(rangeStr, totalPages = 1) {
    const docPages = Math.max(1, totalPages || 1);
    if (!rangeStr || !rangeStr.trim() || rangeStr.trim().toUpperCase() === 'ALL') {
        return '';
    }
    const clean = rangeStr.trim();
    const parts = clean.split(/[,;\s]+/);
    const resultParts = [];
    for (const part of parts) {
        const trimmed = part.trim();
        if (!trimmed)
            continue;
        const match = trimmed.match(/^(\d+)\s*[-:]\s*(\d+)$/);
        if (match) {
            const start = parseInt(match[1], 10);
            const end = parseInt(match[2], 10);
            if (!isNaN(start) && !isNaN(end)) {
                const s = Math.max(1, Math.min(start, end));
                const e = Math.max(start, end);
                if (s > docPages) {
                    resultParts.push(`${docPages}`);
                }
                else {
                    const clampedEnd = Math.min(docPages, e);
                    if (s === clampedEnd) {
                        resultParts.push(`${s}`);
                    }
                    else {
                        resultParts.push(`${s}-${clampedEnd}`);
                    }
                }
            }
        }
        else {
            const single = parseInt(trimmed, 10);
            if (!isNaN(single)) {
                if (single > docPages) {
                    resultParts.push(`${docPages}`);
                }
                else if (single >= 1) {
                    resultParts.push(`${single}`);
                }
            }
        }
    }
    // Remove duplicate parts preserving order
    const uniqueParts = Array.from(new Set(resultParts));
    return uniqueParts.join(', ');
}
//# sourceMappingURL=pricing.js.map